/**
 * The ONE control for a document-metadata value.
 *
 * It exists because there were two. The Add-documents panel rendered a date
 * picker for a DATE, a Yes/No select for a BOOLEAN and a dropdown for a field
 * with a lookup; the document's own page rendered a bare text box for all
 * three. So the same nine mandatory fields on the same kind of document behaved
 * differently depending on which screen you were standing on — and the screen
 * with the worse controls is the one you are forced onto after filing, because
 * that is where a document filed without metadata gets its metadata.
 *
 * That mismatch was not cosmetic. `repository._typed_value` parses a DATE with
 * `datetime.fromisoformat`, so a free-text box let a reader type `1 Sep 2026`
 * and get "'Effective date' must be a date" back from the server, and it let
 * them type anything at all into a field whose lookup declares three legal
 * values.
 *
 * `toInputValue` is the other half. A stored DATE comes back as a full
 * timestamp (`2026-09-01T00:00:00+05:30`); `<input type="date">` accepts only
 * `YYYY-MM-DD` and silently renders anything else as EMPTY — so a date that was
 * saved would read back as unset, and re-saving the form would clear it.
 *
 * ## The widget, and where it comes from
 *
 * A field may declare an `edit_mask` — the transaction column's own, unchanged
 * — and this control honours the same set: a dropdown, a radio group, a
 * checkbox group (stored comma-joined), a single yes/no checkbox with explicit
 * codes, a text area, a URL box. It renders them HERE rather than mounting the
 * transaction `FormRenderer`, because that component draws a whole form — the
 * sections, the auto badges, the action bar — and a repository needs one field
 * at a time, in a panel it already lays out. The widgets themselves are a few
 * lines each; the parts worth sharing (the picker, the display formats, the
 * rules) are shared.
 *
 * The REFERENCE PICKER is shared, and deliberately: `LookupModal` is 800 lines
 * of search, paging, multi-column grids, keyboard handling and semantic-match
 * labelling, and a second one would be a worse one. It reads its transport from
 * `TxnRuntime` context, so a tiny adapter turns the repository client's
 * `metadataLookup` into the shape it expects — the same trick
 * `VisualCriteriaForm` uses for a visual's criteria, which are in exactly this
 * position: a value bound to a master table with no transaction behind it.
 * `allowAdd` is false for the same reason it is there: filing a document must
 * not become a way to create master records.
 */

import { useState } from 'react'
import { DisplayCell, LookupModal, TxnRuntimeProvider } from '../txn'
import type { ColumnDef, LookupResponse, TxnRuntime } from '../txn'
import type { DmsClient, DmsEditMask, DmsField, DmsUser } from './contracts'

export interface MetadataFieldInputProps {
  /** The field definition, when the caller has one. A caller working only from
   *  stored values passes `dataType` instead and gets the type-appropriate
   *  control without the lookup. */
  field?: DmsField
  dataType?: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  style?: React.CSSProperties
  id?: string
  /** The directory, for a USER-typed field. Absent ⇒ the field is a text box,
   *  which is what a host with no directory can honestly offer. */
  users?: DmsUser[]
  /** What a TABLE lookup needs to run: the transport, and which document type
   *  the field belongs to. Absent (or a client that serves no lookup) leaves
   *  such a field as a plain text box — which is what it was before, and an
   *  honest degradation rather than a picker that opens onto nothing. */
  client?: DmsClient
  doctypeId?: string
  /** The other values on the form. A lookup filter can narrow by them
   *  (`{form.OTHER_FIELD}`), so the picker has to send them. */
  formValues?: Record<string, unknown>
}

/**
 * How a person is written in a list of people.
 *
 * The EMAIL is what identifies them. A display name is not unique — two
 * colleagues share one often enough that a picker of bare names is a guess —
 * and in a directory that stores logins rather than full names it can be
 * absent or barely distinguishable. So the email is always shown when it is
 * known, with the name in front of it when it adds something.
 *
 * A key neither directory could resolve renders as itself. That is a departed
 * or external account, and showing the id is honest: the field is stale, not
 * empty.
 */
export function userLabel(user: DmsUser): string {
  const email = String(user.email || '').trim()
  const name = String(user.name || '').trim()
  if (email && name && name.toLowerCase() !== email.toLowerCase()) {
    return `${name} (${email})`
  }
  return email || name || user.user_key
}

export function MetadataFieldInput({
  field, dataType, value, onChange, disabled, style, id, users,
  client, doctypeId, formValues,
}: MetadataFieldInputProps) {
  const type = String(field?.data_type || dataType || 'VARCHAR').toUpperCase()
  const options = field ? lookupOptions(field) : []
  const shown = toInputValue(value, type)
  const mask = editMaskOf(field)

  // The declared WIDGET wins over the type-appropriate default: a designer who
  // asked for radio buttons over three codes gets radio buttons, not the
  // dropdown a static picklist would otherwise draw.
  if (mask) {
    const widget = maskWidget({
      mask, value: shown, onChange, disabled, style, id,
    })
    if (widget) return widget
  }

  // A value from a master TABLE. Rendered as a box plus a picker rather than a
  // dropdown on purpose: a master can have fifty thousand rows, and a <select>
  // of fifty thousand rows is not a control.
  if (field && tableLookupOf(field) && client?.metadataLookup && doctypeId) {
    return (
      <LookupField
        field={field} value={shown} onChange={onChange} disabled={disabled}
        style={style} id={id} client={client} doctypeId={doctypeId}
        formValues={formValues}
      />
    )
  }

  // A person, stored by KEY and shown by NAME. A name changes and two people
  // can share one; the key is what keeps the document pointing at the right
  // person after either happens.
  if (type === 'USER' && users) {
    const known = users.some((u) => u.user_key === shown)
    return (
      <select id={id} value={shown} disabled={disabled} style={style}
              onChange={(event) => onChange(event.target.value)}>
        <option value="">—</option>
        {/* Somebody who has left the project still holds documents. Dropping
            them from the list would silently blank the field on the next
            save. */}
        {!known && shown ? (
          <option value={shown}>{shown} (no longer in this project)</option>
        ) : null}
        {users.map((user) => (
          <option key={user.user_key} value={user.user_key}>
            {userLabel(user)}
          </option>
        ))}
      </select>
    )
  }

  if (options.length) {
    return (
      <select id={id} value={shown} disabled={disabled} style={style}
              onChange={(event) => onChange(event.target.value)}>
        {/* A lookup field can still be unset; the blank option is how it is
            cleared, and how an existing value outside the list stays visible
            rather than being silently re-pointed at the first legal one. */}
        <option value="">—</option>
        {!options.includes(shown) && shown ? (
          <option value={shown}>{shown} (not in the list)</option>
        ) : null}
        {options.map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
    )
  }

  if (type === 'BOOLEAN') {
    return (
      <select id={id} value={shown} disabled={disabled} style={style}
              onChange={(event) => onChange(event.target.value)}>
        <option value="">—</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    )
  }

  if (type === 'JSON') {
    return (
      <textarea id={id} rows={3} value={shown} disabled={disabled} style={style}
                onChange={(event) => onChange(event.target.value)} />
    )
  }

  return (
    <input
      id={id}
      type={type === 'DATE' ? 'date'
        : type === 'DATETIME' ? 'datetime-local'
          : type === 'NUMERIC' ? 'number' : 'text'}
      value={shown}
      disabled={disabled}
      style={style}
      onChange={(event) => onChange(event.target.value)}
    />
  )
}

// ── the declared widget ────────────────────────────────────────────────────

/** A field's `edit_mask`, or null when it declares none. */
export function editMaskOf(field?: DmsField): DmsEditMask | null {
  const raw = field as unknown as { edit_mask?: unknown; edit_mask_json?: unknown }
  const source = raw?.edit_mask ?? raw?.edit_mask_json
  const parsed = typeof source === 'string' ? safeJson(source) : source
  if (!parsed || typeof parsed !== 'object') return null
  const mask = String((parsed as DmsEditMask).mask || '').trim()
  if (!mask) return null
  return parsed as DmsEditMask
}

function maskValues(mask: DmsEditMask): Array<{ data: string; display: string }> {
  return (mask.values || []).map((v) => ({
    data: String(v?.data ?? ''),
    display: String(v?.display ?? v?.data ?? ''),
  }))
}

/**
 * Render the widget a field's `edit_mask` asks for, or null to fall through to
 * the type-appropriate control.
 *
 * `image` / `document` deliberately fall through: a repository already stores
 * files as IMAGE-typed metadata through its own rights-checked blob path, and
 * drawing a second uploader here would put a document's picture into the flat
 * project file store instead — outside the repository that governs it.
 */
function maskWidget({ mask, value, onChange, disabled, style, id }: {
  mask: DmsEditMask
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  style?: React.CSSProperties
  id?: string
}): React.ReactElement | null {
  const kind = String(mask.mask || '')
  const values = maskValues(mask)

  if (kind === 'ddlb') {
    return (
      <select id={id} value={value} disabled={disabled} style={style}
              onChange={(event) => onChange(event.target.value)}>
        <option value="">—</option>
        {/* A value the list no longer offers stays visible rather than being
            silently re-pointed at the first legal one — the same courtesy the
            static picklist extends. */}
        {value && !values.some((v) => v.data === value) ? (
          <option value={value}>{value} (not in the list)</option>
        ) : null}
        {values.map((v, index) => (
          <option key={`${v.data}-${index}`} value={v.data}>{v.display}</option>
        ))}
      </select>
    )
  }

  if (kind === 'radio') {
    return (
      <div id={id} style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px' }}>
        {values.map((v, index) => (
          <label key={`${v.data}-${index}`}
                 style={{ display: 'flex', alignItems: 'center', gap: 5,
                          cursor: disabled ? 'not-allowed' : 'pointer' }}>
            <input type="radio" value={v.data} checked={value === v.data}
                   disabled={disabled}
                   onChange={() => onChange(v.data)} />
            {v.display}
          </label>
        ))}
      </div>
    )
  }

  if (kind === 'checkbox') {
    // A multi-select group. The aggregate is one comma-joined string, which is
    // what keeps the field a plain VARCHAR — the same storage decision the
    // transaction column makes, so the two read each other's values.
    const selected = splitCsv(value)
    const toggle = (data: string, on: boolean) => {
      const next = selected.filter((s) => s !== data)
      if (on) next.push(data)
      onChange(next.join(','))
    }
    return (
      <div id={id} style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px' }}>
        {values.map((v, index) => (
          <label key={`${v.data}-${index}`}
                 style={{ display: 'flex', alignItems: 'center', gap: 5,
                          cursor: disabled ? 'not-allowed' : 'pointer' }}>
            <input type="checkbox" checked={selected.includes(v.data)}
                   disabled={disabled}
                   onChange={(event) => toggle(v.data, event.target.checked)} />
            {v.display}
          </label>
        ))}
      </div>
    )
  }

  if (kind === 'bool_check') {
    // One checkbox with EXPLICIT stored codes — slot 0 is what "ticked" saves,
    // slot 1 what "unticked" saves. Y/N is only the usual pair, not the rule.
    const on = values[0]?.data ?? 'Y'
    const off = values[1]?.data ?? 'N'
    return (
      <input id={id} type="checkbox" checked={value === on} disabled={disabled}
             style={{ width: 18, height: 18 }}
             onChange={(event) => onChange(event.target.checked ? on : off)} />
    )
  }

  if (kind === 'textarea') {
    return (
      <textarea id={id} rows={3} value={value} disabled={disabled} style={style}
                onChange={(event) => onChange(event.target.value)} />
    )
  }

  if (kind === 'url') {
    return (
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input id={id} type="url" value={value} disabled={disabled}
               placeholder="https://…"
               style={{ ...(style || {}), flex: 1 }}
               onChange={(event) => onChange(event.target.value)} />
        {/* Only for something that can actually be opened — a half-typed
            address must not offer a link that goes nowhere. */}
        {/^https?:\/\//i.test(value.trim()) ? (
          <a href={value.trim()} target="_blank" rel="noreferrer"
             style={{ fontSize: '0.8125rem' }}>Open</a>
        ) : null}
      </div>
    )
  }

  // 'textbox', 'image', 'document', or anything this build does not know:
  // fall through to the control the data type deserves.
  return null
}

function splitCsv(value: string): string[] {
  return String(value || '').split(',').map((s) => s.trim()).filter(Boolean)
}

// ── the reference picker ───────────────────────────────────────────────────

/** `TABLE(DISPLAY, DATA)` when this field reads its values from a master table.
 *  A STATIC picklist returns '' — those are values, not rows. */
export function tableLookupOf(field: DmsField): string {
  const raw = field as unknown as {
    lookup?: unknown; has_lookup?: unknown
  }
  const spec = typeof raw.lookup === 'string' ? raw.lookup.trim() : ''
  if (spec) return spec
  // A SQL-mode lookup has no spec to show; `has_lookup` is how the server says
  // there is one anyway.
  return raw.has_lookup ? ' ' : ''
}

function LookupField({
  field, value, onChange, disabled, style, id, client, doctypeId, formValues,
}: {
  field: DmsField
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  style?: React.CSSProperties
  id?: string
  client: DmsClient
  doctypeId: string
  formValues?: Record<string, unknown>
}) {
  const [open, setOpen] = useState(false)
  const label = field.title || field.field_key
  const spec = String((field as { lookup?: string }).lookup || '').trim()

  // LookupModal reads its transport from TxnRuntime context. A repository host
  // provides ONE member of it — the lookup — and the rest are never reached
  // because `allowAdd` is false; stubbing them loudly is better than a
  // half-real object that fails somewhere obscure if that ever changes.
  const lookup: TxnRuntime['lookup'] = async (
    _project: string, _objName: string, _lookup: string,
    search?: string, limit?: number,
  ) => {
    const res = await client.metadataLookup!({
      doctypeId, fieldKey: field.field_key,
      search: search || '', limit: limit || 50,
      values: formValues || {},
    })
    return {
      ok: true,
      rows: (res.rows || []).map((r) => ({
        display: String(r.display ?? ''), data: r.data,
        ...(r.cells ? { cells: r.cells } : {}),
      })),
      total: res.total ?? (res.rows || []).length,
      table: '', display_col: res.display_col || '',
      data_col: res.data_col || '',
      ...(res.columns ? { columns: res.columns } : {}),
    } as LookupResponse
  }

  const runtime = {
    lookup,
    notify: { success: () => {}, error: () => {} },
  } as unknown as TxnRuntime

  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input
        id={id} type="text" value={value} disabled={disabled}
        placeholder="Pick from the list…"
        style={{ ...(style || {}), flex: 1 }}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          // F2 opens the picker — the shortcut the transaction form uses, so
          // somebody who knows one screen knows this one.
          if (event.key === 'F2' && !disabled) {
            event.preventDefault()
            setOpen(true)
          }
        }}
      />
      <button type="button" disabled={disabled}
              onClick={() => { if (!disabled) setOpen(true) }}
              title={`Look up ${label} (F2)`}
              aria-label={`Look up ${label}`}
              style={{ padding: '4px 9px', cursor: disabled ? 'not-allowed' : 'pointer',
                       border: '1px solid var(--color-border, #d0d0d0)',
                       borderRadius: 4, background: 'transparent' }}>
        🔍
      </button>
      {open ? (
        <TxnRuntimeProvider value={runtime}>
          <LookupModal
            open
            project=""
            objName=""
            lookup={spec}
            fieldLabel={label}
            title={(field as { lookup_title?: string }).lookup_title || undefined}
            dataCol={(field as { lookup_sql_data_col?: string }).lookup_sql_data_col}
            // Filing a document must not become a way to create master records
            // — and the repository host provides no save transport anyway.
            allowAdd={false}
            formValues={formValues}
            onClose={() => setOpen(false)}
            onSelect={(row) => {
              onChange(row.data == null ? '' : String(row.data))
              setOpen(false)
            }}
          />
        </TxnRuntimeProvider>
      ) : null}
    </div>
  )
}

/**
 * What the control should show for a stored value.
 *
 * Only DATE and BOOLEAN need it, and both for the same reason: the server
 * stores a shape the HTML control will not accept, and an unaccepted value
 * renders as empty rather than as an error — which reads as "nobody filled
 * this in".
 */
export function toInputValue(value: unknown, dataType: string): string {
  if (value === null || value === undefined) return ''
  const text = String(value)
  const type = String(dataType || '').toUpperCase()
  if (type === 'DATE') {
    // `2026-09-01T00:00:00+05:30` and `2026-09-01 00:00:00` both start with
    // the calendar date, which is the whole of what the control wants. Parsing
    // it as a Date would be wrong: that shifts the day across a timezone.
    const match = /^(\d{4}-\d{2}-\d{2})/.exec(text)
    return match ? match[1] : text
  }
  if (type === 'DATETIME') {
    // The same reasoning one field wider: `datetime-local` wants exactly
    // `YYYY-MM-DDTHH:MM`, and the seconds and the offset are not its business.
    const match = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/.exec(text)
    return match ? `${match[1]}T${match[2]}` : text
  }
  if (type === 'BOOLEAN') {
    const lower = text.trim().toLowerCase()
    if (['1', 'true', 'yes', 'y', 'on'].includes(lower)) return 'true'
    if (['0', 'false', 'no', 'n', 'off'].includes(lower)) return 'false'
    return ''
  }
  return text
}

/**
 * A field's legal values.
 *
 * The server resolves a static picklist for us and sends the choices in
 * `values`, sending `lookup` EMPTY — it keeps that key for a `TABLE(...)` spec,
 * which is a list of rows rather than a list of values. Reading `lookup` first
 * therefore let `''` — what `field_rules.describe` sends for every static
 * picklist there is — shadow the populated `lookup_json` beside it under `??`,
 * which only falls through on null. Business area, Audience, Classification
 * and Retention label each drew a free-text box with no sign of what may go in
 * it, and a word typed into that box comes back from `check_legal_value` as
 * "'Classification' must be one of: …" — so the cost was not cosmetic: it was
 * a detail nobody could fill in.
 *
 * The two JSON columns stay as the fallback, for a host that describes a field
 * without resolving it.
 */
export function lookupOptions(field: DmsField): string[] {
  const raw = field as unknown as {
    values?: unknown; lookup_json?: unknown; lookup?: unknown
  }
  if (Array.isArray(raw.values) && raw.values.length) {
    return raw.values.map((v) => String(v))
  }
  const source = isPresent(raw.lookup) ? raw.lookup : raw.lookup_json
  const parsed = typeof source === 'string' ? safeJson(source) : source
  if (Array.isArray(parsed)) return parsed.map((v) => String(v))
  if (parsed && typeof parsed === 'object') {
    const values = (parsed as { values?: unknown }).values
    if (Array.isArray(values)) return values.map((v) => String(v))
  }
  return []
}

/** Whether a JSON column carries anything — null, undefined and a blank string
 *  all carry nothing. */
function isPresent(value: unknown): boolean {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim() !== ''
  return true
}

function safeJson(raw: string): unknown {
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

// ── declared defaults ──────────────────────────────────────────────────────
//
// The document TYPE says what its metadata is, so nobody retypes the owner,
// the classification and the review date on every upload. The server applies
// these on `upload` regardless (`repository.apply_field_defaults`); resolving
// them here too is what lets the panel SHOW the value before the file is
// filed, and lets the person change it.
//
// Most of the vocabulary is NOT resolvable in a browser — a project variable
// is a query, an audit stamp is a server clock, the filer's own name is a
// directory lookup — so the server resolves each field's default once when it
// describes the type and sends it as `default_preview`. That is preferred over
// anything worked out here; the local expansion below is the fallback for a
// host that does not send one, and for the plain date tokens where a round
// trip would be silly. `defaultIsServerSide` reports the remaining case, so
// the panel can say "filled on upload" rather than showing an empty box.

/** Whether a type declares any default for this field. */
export function hasFieldDefault(field: DmsField): boolean {
  return rawDefault(field) !== null || !!serverPreview(field)
}

/** The value the SERVER resolved for this field, when it sent one. */
function serverPreview(field: DmsField): string {
  const preview = (field as { default_preview?: unknown }).default_preview
  return typeof preview === 'string' ? preview : ''
}

/**
 * SYSTEM-FILLED: the repository fills this field and the person filing is not
 * offered a box for it. Enforced on the server (`repository.set_metadata`
 * discards a posted value); what this drives is only whether an input is
 * DRAWN, which is a courtesy, not the control.
 */
export function isProtectedField(field: DmsField): boolean {
  const value = (field as { protect?: unknown }).protect
  return value === true || value === 1 || value === '1'
    || String(value).toLowerCase() === 'true'
}

/**
 * True when the value cannot exist until the document does — a serial. The
 * form says so instead of showing a blank box or, worse, a number it made up:
 * the reference number is allocated at the moment of filing, once.
 */
export function generatedOnFiling(field: DmsField): boolean {
  if ((field as { generated_on_filing?: unknown }).generated_on_filing === true) {
    return true
  }
  const raw = rawDefault(field)
  return typeof raw === 'string' && /^\s*auto_generate\w*\s*\(\s*\)\s*$/i.test(raw)
}

/** True when the default exists but only the server can resolve it. */
export function defaultIsServerSide(field: DmsField): boolean {
  if (serverPreview(field)) return false
  if (generatedOnFiling(field)) return true
  const raw = rawDefault(field)
  if (typeof raw !== 'string') return false
  const text = raw.trim()
  // A `:audit_*` / `:calendar_*` / `:<project variable>` token, or a function
  // call this side does not evaluate. The one family the browser DOES resolve
  // is the `{{...}}` set below, minus the identity tokens.
  if (text.startsWith(':')) return true
  if (/^[A-Za-z_]\w*\s*\(.*\)$/.test(text)) return true
  return /\{\{(user|user_id|user_name|user_email|tenant|ip)\}\}/.test(text)
}

/**
 * The default this field takes for a document being filed now, or `''` when
 * the type declares none or only the server can work it out.
 *
 * The token vocabulary matches `auto_file_rule`'s, and `{{today+12m}}` /
 * `{{today+30d}}` / `{{today+2y}}` exist because a review date is a period
 * after filing far more often than it is a fixed day.
 */
export function resolveFieldDefault(field: DmsField, context: {
  doctypeKey?: string
  title?: string
  filename?: string
  /** The signed-in person's KEY, when the host knows it.
   *
   *  `{{user}}` / `{{user_id}}` store a user key, and a host rendering this
   *  form already knows whose session it is — so a field defaulted to the
   *  filer can arrive filled in rather than empty. Only these two tokens are
   *  resolved here: `{{user_name}}` and `{{user_email}}` are directory facts
   *  this side does not hold, and guessing them would put a different value in
   *  the box from the one the server will store. */
  userKey?: string
  /** Injectable so a test is not at the mercy of the clock. */
  today?: Date
} = {}): string {
  // The server already resolved the whole vocabulary for this field when it
  // described the type. Prefer it — it is the only side that can.
  const preview = serverPreview(field)
  if (preview) return preview
  const raw = rawDefault(field)
  if (raw === null) return ''
  if (typeof raw !== 'string') return String(raw)

  const now = context.today ?? new Date()
  const text = raw.trim()

  const offset = /^\{\{today([+-]\d+)([dmy])\}\}$/.exec(text)
  if (offset) {
    const amount = Number(offset[1])
    const unit = offset[2]
    if (unit === 'd') return isoDate(addDays(now, amount))
    if (unit === 'm') return isoDate(addMonths(now, amount))
    return isoDate(addMonths(now, amount * 12))
  }

  const replacements: Record<string, string> = {
    '{{today}}': isoDate(now),
    '{{year}}': String(now.getFullYear()),
    '{{doctype}}': context.doctypeKey || '',
    '{{title}}': context.title || '',
    '{{filename}}': context.filename || '',
  }
  // Only when the host actually supplied it. Without a key these stay
  // unresolved and fall to the server, exactly as they did before.
  if (context.userKey) {
    replacements['{{user}}'] = context.userKey
    replacements['{{user_id}}'] = context.userKey
  }
  let out = text
  for (const [token, value] of Object.entries(replacements)) {
    out = out.split(token).join(value)
  }
  // A token this side cannot resolve (the identity set — `{{user_name}}` and
  // friends, which the server fills) leaves the box empty rather than showing
  // a literal `{{user_name}}` the person would have to delete.
  if (/\{\{[^}]*\}\}/.test(out)) return ''
  return out
}

function rawDefault(field: DmsField): unknown {
  const source = (field as { default_json?: unknown; default?: unknown })
  let raw = source.default ?? source.default_json
  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    // The column holds JSON, so a plain string arrives quoted; anything that
    // is not JSON is taken as the literal default it looks like.
    if (trimmed.startsWith('{') || trimmed.startsWith('[')
        || trimmed.startsWith('"')) {
      const parsed = safeJson(trimmed)
      if (parsed !== null) raw = parsed
    }
  }
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    raw = (raw as { value?: unknown }).value
  }
  if (raw === null || raw === undefined || raw === '') return null
  return raw
}

/** Local calendar date, never `toISOString()` — that converts to UTC and can
 *  hand back yesterday for anyone east of it. */
function isoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

function addDays(date: Date, days: number): Date {
  const out = new Date(date.getTime())
  out.setDate(out.getDate() + days)
  return out
}

/** Clamped to the end of a short month, so 31 Jan + 1 month is 28 Feb rather
 *  than rolling into March the way `setMonth` alone would. */
function addMonths(date: Date, months: number): Date {
  const out = new Date(date.getTime())
  const day = out.getDate()
  out.setDate(1)
  out.setMonth(out.getMonth() + months)
  const lastDay = new Date(out.getFullYear(), out.getMonth() + 1, 0).getDate()
  out.setDate(Math.min(day, lastDay))
  return out
}

/**
 * A stored value, RENDERED — a status tag, a bar, a flag, a link, a rating.
 *
 * The formats are the transaction column's own and so is the renderer: this
 * mounts the shared `<DisplayCell>` rather than reimplementing thirty formats,
 * which is the whole reason a repository's list and a transaction's list agree
 * about what "overdue in red" looks like. Without a declared format it falls
 * back to `displayMetadataValue` — the plain text a reader saw before.
 *
 * `row` is the other metadata values: the composite formats (a date range, a
 * two-line cell, a progress bar with its maximum in another column) read a
 * companion column out of it.
 */
export function MetadataValue({ field, value, dataType, users, row }: {
  field?: DmsField
  value: unknown
  dataType?: string
  users?: DmsUser[]
  row?: Record<string, unknown>
}): React.ReactElement {
  const type = String(field?.data_type || dataType || 'VARCHAR')
  const format = displayFormatOf(field)
  // A widget stores a CODE and shows a label. Read-only must show the label
  // too, or a field captured as "Active / Inactive" reads back as "A".
  const text = maskLabelFor(field, value) ?? displayMetadataValue(value, type, users)
  if (!format) return <>{text}</>
  // A USER field resolved to a NAME, a BOOLEAN to true/false, a DATE to its
  // calendar day — the format then works on what a person would read rather
  // than on the bookkeeping key underneath.
  const col = {
    name: field?.field_key || '', db_name: field?.field_key || '',
    heading: field?.title || field?.field_key || '',
    display: format,
  } as unknown as ColumnDef
  return <DisplayCell value={text || value} col={col} row={row} />
}

/**
 * The LABEL a widget's stored code stands for, or null when there is no widget
 * (or the code is not one of its values, which is a stale value worth showing
 * as itself rather than hiding).
 *
 * A checkbox group holds several codes comma-joined, so each is translated and
 * they are re-joined for reading.
 */
export function maskLabelFor(field: DmsField | undefined,
                             value: unknown): string | null {
  const mask = editMaskOf(field)
  if (!mask) return null
  const values = maskValues(mask)
  if (!values.length) return null
  const label = (code: string) =>
    values.find((v) => v.data === code)?.display ?? code
  const text = value == null ? '' : String(value)
  if (!text) return ''
  if (mask.mask === 'checkbox') {
    return splitCsv(text).map(label).join(', ')
  }
  return label(text)
}

/** A field's list/panel display format, or null when it declares none. */
export function displayFormatOf(field?: DmsField): Record<string, unknown> | null {
  const raw = field as unknown as { display?: unknown; display_json?: unknown }
  const source = raw?.display ?? raw?.display_json
  const parsed = typeof source === 'string' ? safeJson(source) : source
  if (!parsed || typeof parsed !== 'object') return null
  const type = String((parsed as { type?: unknown }).type || '').trim()
  // 'text' IS the fallback, so treating it as "no format" keeps one code path
  // for the overwhelmingly common case.
  if (!type || type === 'text') return null
  return parsed as Record<string, unknown>
}

/** The name to SHOW for a stored value — a user key resolved through the
 *  directory, anything else unchanged. Falls back to the key so a departed
 *  owner reads as a stale reference rather than as an empty field. */
export function displayMetadataValue(value: unknown, dataType: string,
                                     users?: DmsUser[]): string {
  const text = toInputValue(value, dataType)
  if (!text) return ''
  if (String(dataType || '').toUpperCase() !== 'USER' || !users) return text
  const found = users.find((u) => u.user_key === text)
  return found ? userLabel(found) : text
}
